import { FeatureFlags } from './FeatureFlags.js';
import campaignDefinition from '../data/campaign/campaign-v1.json';
import { EventBus } from '../engine/EventBus.js';
import { Clock } from '../engine/Clock.js';
import { DeterministicRandom } from '../engine/DeterministicRandom.js';
import { EntityWorld } from '../engine/entities/EntityWorld.js';
import { TraversalHuntingSystem } from '../engine/hunting/TraversalHuntingSystem.js';
import { ReplayDivergenceDetector } from '../engine/replay/ReplayDivergenceDetector.js';
import { BrowserSaveStorage } from '../engine/saves/BrowserSaveStorage.js';
import { SaveSystem } from '../engine/saves/SaveSystem.js';
import { resetSimulationSession } from '../engine/SimulationSessionReset.js';
import { SimulationStepSnapshotStore } from '../engine/SimulationStepSnapshotStore.js';
import { InputManager } from '../engine/input/InputManager.js';
import { InputReplaySystem } from '../engine/input/InputReplaySystem.js';
import { SnakeState } from '../engine/snake/SnakeState.js';
import { buildStoryEvents } from '../engine/story/StoryEventBridge.js';
import { StorySystem } from '../engine/story/StorySystem.js';
import { TraversalWorld } from '../engine/traversal/TraversalWorld.js';
import { WildlifeSystem } from '../engine/wildlife/WildlifeSystem.js';
import { LegacyV31Adapter } from '../compat/LegacyV31Adapter.js';
import { installTestApi } from '../devtools/TestApi.js';
import { TestLab } from '../devtools/TestLab.js';

export class GameApp {
  constructor({ mount }) {
    this.mount = mount;
    this.bus = new EventBus();
    this.clock = new Clock();
    this.random = new DeterministicRandom();
    this.input = new InputManager();
    this.inputReplay = new InputReplaySystem();
    this.replayDivergence = new ReplayDivergenceDetector();
    this.entityWorld = new EntityWorld();
    this.traversalWorld = new TraversalWorld();
    this.snakeState = new SnakeState({
      entityWorld: this.entityWorld,
      traversalWorld: this.traversalWorld,
    });
    this.wildlifeSystem = new WildlifeSystem({
      entityWorld: this.entityWorld,
      traversalWorld: this.traversalWorld,
    });
    this.huntingSystem = new TraversalHuntingSystem({
      entityWorld: this.entityWorld,
      traversalWorld: this.traversalWorld,
      snakeState: this.snakeState,
      wildlifeSystem: this.wildlifeSystem,
    });
    this.storySystem = new StorySystem({ definition: campaignDefinition });
    this.simulationSnapshots = new SimulationStepSnapshotStore();
    this.features = new FeatureFlags();
    this.runtimeState = {
      activeScenario: null,
      timeOfDay: 'day',
      weather: 'clear',
      simulationSteps: 0,
      simulationStep: null,
      simulationSnapshots: this.simulationSnapshots.getStatus(),
      entityWorld: this.entityWorld.getStatus(),
      traversalWorld: this.traversalWorld.getStatus(),
      snakeState: this.snakeState.getStatus(),
      wildlifeSystem: this.wildlifeSystem.getStatus(),
      huntingSystem: this.huntingSystem.getStatus(),
      storySystem: this.storySystem.getStatus(),
      random: this.random.snapshot(),
      input: this.input.getSnapshot(),
      inputReplay: this.inputReplay.getStatus(),
      replayDivergence: this.replayDivergence.getStatus(),
      saveSystem: null,
    };
    this.saveSystem = new SaveSystem({
      clock: this.clock,
      random: this.random,
      simulationSnapshots: this.simulationSnapshots,
      entityWorld: this.entityWorld,
      traversalWorld: this.traversalWorld,
      snakeState: this.snakeState,
      wildlifeSystem: this.wildlifeSystem,
      huntingSystem: this.huntingSystem,
      storySystem: this.storySystem,
      storage: new BrowserSaveStorage(),
      captureEnvironment: () => ({
        activeScenario: this.runtimeState.activeScenario,
        timeOfDay: this.runtimeState.timeOfDay,
        weather: this.runtimeState.weather,
      }),
      restoreEnvironment: (environment) => this.#restoreEnvironment(environment),
      afterLoad: (details) => this.#afterSaveLoad(details),
      buildInfo: {
        channel: __BUILD_CHANNEL__,
        commit: __BUILD_COMMIT__,
      },
    });
    this.runtimeState.saveSystem = this.saveSystem.getStatus();
    this.lastFrame = 0;
  }

  start() {
    this.detachShellInput = this.input.attach();
    this.#renderShell();
    this.#wireEvents();
    this.testApi = installTestApi({
      bus: this.bus,
      clock: this.clock,
      random: this.random,
      input: this.input,
      inputReplay: this.inputReplay,
      replayDivergence: this.replayDivergence,
      entityWorld: this.entityWorld,
      traversalWorld: this.traversalWorld,
      snakeState: this.snakeState,
      wildlifeSystem: this.wildlifeSystem,
      huntingSystem: this.huntingSystem,
      storySystem: this.storySystem,
      saveSystem: this.saveSystem,
      simulationSnapshots: this.simulationSnapshots,
      featureFlags: this.features,
      advanceSimulation: (seconds) => {
        const steps = this.clock.advance(seconds, (delta, time) => this.#fixedStep(delta, time));
        return {
          ok: true,
          steps,
          clock: this.clock.snapshot(),
          random: this.random.snapshot(),
          inputReplay: this.inputReplay.getStatus(),
          replayDivergence: this.replayDivergence.getStatus(),
          entityWorld: this.entityWorld.getStatus(),
          traversalWorld: this.traversalWorld.getStatus(),
          snakeState: this.snakeState.getStatus(),
          wildlifeSystem: this.wildlifeSystem.getStatus(),
          huntingSystem: this.huntingSystem.getStatus(),
          storySystem: this.storySystem.getStatus(),
          saveSystem: this.saveSystem.getStatus(),
          simulationSnapshots: this.simulationSnapshots.getStatus(),
          latestSimulationStep: this.simulationSnapshots.getLatest(),
        };
      },
      resetSession: (options) => this.#resetSession(options),
      getInputSnapshot: () => this.runtimeState.input,
      getSnapshot: () => ({
        ...this.runtimeState,
        random: this.random.snapshot(),
        replayDivergence: this.replayDivergence.getStatus(),
        entityWorld: this.entityWorld.getStatus(),
        traversalWorld: this.traversalWorld.getStatus(),
        snakeState: this.snakeState.getStatus(),
        wildlifeSystem: this.wildlifeSystem.getStatus(),
        huntingSystem: this.huntingSystem.getStatus(),
        storySystem: this.storySystem.getStatus(),
        saveSystem: this.saveSystem.getStatus(),
      }),
    });
    this.testLab = new TestLab({ bus: this.bus, testApi: this.testApi });
    requestAnimationFrame((timestamp) => this.#frame(timestamp));
  }

  #renderShell() {
    this.mount.innerHTML = `
      <main class="shell">
        <header class="topbar">
          <div>
            <p class="eyebrow">SERPENT: BORN WILD</p>
            <h1>Engine 2.0 Development Platform</h1>
          </div>
          <div class="build-badge">${__BUILD_CHANNEL__}</div>
        </header>
        <section class="hero">
          <div>
            <h2>v32 compatibility shell is running.</h2>
            <p>The existing v31 game remains isolated while systems are extracted into deterministic modules.</p>
            <div class="actions">
              <button type="button" data-action="legacy">Launch v31 Baseline</button>
              <button type="button" data-action="test-lab">Open Test Lab</button>
            </div>
          </div>
          <dl class="status-grid">
            <div><dt>Engine 2.0</dt><dd>Enabled</dd></div>
            <div><dt>Simulation</dt><dd data-status="simulation">Running</dd></div>
            <div><dt>Input</dt><dd data-status="input">Ready</dd></div>
            <div><dt>Replay</dt><dd data-status="replay">Idle</dd></div>
            <div><dt>Scenario</dt><dd data-status="scenario">None</dd></div>
            <div><dt>Commit</dt><dd>${__BUILD_COMMIT__.slice(0, 8)}</dd></div>
          </dl>
        </section>
        <section class="legacy-host" data-legacy-host>
          <div class="legacy-placeholder">
            <strong>v31 compatibility baseline</strong>
            <span>Import the legacy build with scripts/IMPORT_V31.ps1.</span>
          </div>
        </section>
        <nav class="play-controls" data-play-controls hidden aria-label="Game development controls">
          <button type="button" class="play-controls__tab" data-action="controls-toggle" aria-expanded="false">DEV</button>
          <div class="play-controls__menu" data-controls-menu hidden>
            <button type="button" data-action="exit-game">Exit Game</button>
            <button type="button" data-action="test-lab">Test Lab</button>
          </div>
        </nav>
      </main>
    `;

    const host = this.mount.querySelector('[data-legacy-host]');
    this.shell = this.mount.querySelector('.shell');
    this.playControls = this.mount.querySelector('[data-play-controls]');
    this.controlsMenu = this.mount.querySelector('[data-controls-menu]');
    this.controlsToggle = this.mount.querySelector('[data-action="controls-toggle"]');
    this.legacy = new LegacyV31Adapter({ mount: host, input: this.input });

    this.mount.addEventListener('click', (event) => {
      const action = event.target.closest('[data-action]')?.dataset.action;
      if (action === 'legacy') {
        this.legacy.launch();
        this.#setPlayMode(true);
      }
      if (action === 'exit-game') this.#setPlayMode(false);
      if (action === 'controls-toggle') {
        const opening = this.controlsMenu.hidden;
        this.controlsMenu.hidden = !opening;
        this.controlsToggle.setAttribute('aria-expanded', String(opening));
      }
      if (action === 'test-lab') {
        this.controlsMenu.hidden = true;
        this.controlsToggle?.setAttribute('aria-expanded', 'false');
        this.testLab?.toggle(true);
      }
    });
  }

  #setPlayMode(active) {
    this.shell.classList.toggle('is-playing', active);
    document.body.classList.toggle('game-active', active);
    this.playControls.hidden = !active;
    this.controlsMenu.hidden = true;
    this.controlsToggle.setAttribute('aria-expanded', 'false');
    if (active) this.legacy.focus();
    else window.scrollTo({ top: 0, behavior: 'instant' });
  }

  #restoreEnvironment(environment) {
    if (!environment || typeof environment !== 'object' || Array.isArray(environment)) {
      throw new TypeError('save environment must be an object');
    }
    const activeScenario = environment.activeScenario ?? null;
    if (activeScenario !== null
      && (typeof activeScenario !== 'string' || activeScenario.trim().length === 0)) {
      throw new TypeError('save environment activeScenario must be null or a non-empty string');
    }
    for (const key of ['timeOfDay', 'weather']) {
      if (typeof environment[key] !== 'string' || environment[key].trim().length === 0) {
        throw new TypeError(`save environment ${key} must be a non-empty string`);
      }
    }
    this.runtimeState.activeScenario = activeScenario;
    this.runtimeState.timeOfDay = environment.timeOfDay;
    this.runtimeState.weather = environment.weather;
  }

  #afterSaveLoad(details) {
    const inputState = this.input.reset();
    const inputReplay = this.inputReplay.reset();
    const replayDivergence = this.replayDivergence.reset();
    const simulationSnapshots = this.simulationSnapshots.getStatus();
    Object.assign(this.runtimeState, {
      simulationSteps: simulationSnapshots.nextTick,
      simulationStep: null,
      simulationSnapshots,
      entityWorld: this.entityWorld.getStatus(),
      traversalWorld: this.traversalWorld.getStatus(),
      snakeState: this.snakeState.getStatus(),
      wildlifeSystem: this.wildlifeSystem.getStatus(),
      huntingSystem: this.huntingSystem.getStatus(),
      storySystem: this.storySystem.getStatus(),
      random: this.random.snapshot(),
      input: inputState,
      inputReplay,
      replayDivergence,
    });
    this.lastFrame = 0;
    const scenarioStatus = this.mount.querySelector('[data-status="scenario"]');
    if (scenarioStatus) scenarioStatus.textContent = this.runtimeState.activeScenario ?? 'None';
    this.legacy?.forwardInput(inputState);
    this.bus.emit('save:loaded', details);
  }

  #wireEvents() {
    this.bus.on('scenario:start', (scenario) => {
      this.runtimeState.activeScenario = scenario.id;
      this.mount.querySelector('[data-status="scenario"]').textContent = scenario.label;
    });
    this.bus.on('world:set-time', ({ timeOfDay }) => { this.runtimeState.timeOfDay = timeOfDay; });
    this.bus.on('world:set-weather', ({ weather }) => { this.runtimeState.weather = weather; });
    this.bus.on('entity-world:changed', () => {
      this.runtimeState.entityWorld = this.entityWorld.getStatus();
    });
    this.bus.on('traversal-world:changed', () => {
      this.runtimeState.traversalWorld = this.traversalWorld.getStatus();
    });
    this.bus.on('snake-state:changed', () => {
      this.runtimeState.snakeState = this.snakeState.getStatus();
      this.runtimeState.traversalWorld = this.traversalWorld.getStatus();
      this.runtimeState.entityWorld = this.entityWorld.getStatus();
    });
    this.bus.on('wildlife-state:changed', () => {
      this.runtimeState.wildlifeSystem = this.wildlifeSystem.getStatus();
      this.runtimeState.traversalWorld = this.traversalWorld.getStatus();
      this.runtimeState.entityWorld = this.entityWorld.getStatus();
    });
    this.bus.on('hunting-state:changed', () => {
      this.runtimeState.huntingSystem = this.huntingSystem.getStatus();
      this.runtimeState.snakeState = this.snakeState.getStatus();
      this.runtimeState.wildlifeSystem = this.wildlifeSystem.getStatus();
      this.runtimeState.traversalWorld = this.traversalWorld.getStatus();
      this.runtimeState.entityWorld = this.entityWorld.getStatus();
    });
    this.bus.on('story-state:changed', () => {
      this.runtimeState.storySystem = this.storySystem.getStatus();
    });
    this.bus.on('save-state:changed', () => {
      this.runtimeState.saveSystem = this.saveSystem.getStatus();
    });
  }

  #resetSession(options = {}) {
    const result = resetSimulationSession({
      clock: this.clock,
      random: this.random,
      input: this.input,
      inputReplay: this.inputReplay,
      simulationSnapshots: this.simulationSnapshots,
      entityWorld: this.entityWorld,
      traversalWorld: this.traversalWorld,
      snakeState: this.snakeState,
      wildlifeSystem: this.wildlifeSystem,
      huntingSystem: this.huntingSystem,
      storySystem: this.storySystem,
    }, options);
    const replayDivergence = this.replayDivergence.reset();
    Object.assign(this.runtimeState, result.runtime, {
      replayDivergence,
      saveSystem: this.saveSystem.getStatus(),
    });
    this.lastFrame = 0;
    this.mount.querySelector('[data-status="scenario"]').textContent = 'None';
    this.legacy?.forwardInput(result.runtime.input);
    this.bus.emit('simulation:reset', result);
    return result;
  }

  #fixedStep(step, time) {
    const physicalInput = this.input.sample(time * 1000);
    const replayInput = this.inputReplay.nextFrame();
    const effectiveInput = replayInput || physicalInput;
    this.inputReplay.capture(effectiveInput);
    const replayStatus = this.inputReplay.getStatus();
    const randomState = this.random.beginTick();
    const tick = this.simulationSnapshots.getStatus().nextTick;

    this.snakeState.step({ stepSeconds: step, tick, simulationTime: time, input: effectiveInput });
    const huntingStep = this.huntingSystem.step({
      stepSeconds: step,
      tick,
      simulationTime: time,
      input: effectiveInput,
    });
    this.wildlifeSystem.step({
      stepSeconds: step,
      tick,
      simulationTime: time,
      snakeState: this.snakeState.snapshot(),
      tickSeed: randomState.tickSeed,
    });
    const traversalWorldStateRef = this.traversalWorld.beginStep({
      tick,
      simulationTime: time,
      stepSeconds: step,
    });
    const storyEvents = buildStoryEvents({
      tick,
      simulationTime: time,
      input: effectiveInput,
      huntingStep,
      snakeState: this.snakeState.snapshot(),
      storySystem: this.storySystem,
      traversalWorld: this.traversalWorld,
      wildlifeSystem: this.wildlifeSystem,
    });
    const storyStep = this.storySystem.step({
      stepSeconds: step,
      tick,
      simulationTime: time,
      events: storyEvents,
    });
    if (storyStep.changed || storyStep.completedMissionIds.length > 0) {
      this.bus.emit('story:step', storyStep);
    }
    const entityWorldStateRef = this.entityWorld.beginStep({
      tick,
      simulationTime: time,
      stepSeconds: step,
    });
    const worldStateRef = Object.freeze({
      ...entityWorldStateRef,
      traversalWorld: traversalWorldStateRef,
      snakeState: this.snakeState.getStateRef(),
      wildlifeSystem: this.wildlifeSystem.getStateRef(),
      huntingSystem: this.huntingSystem.getStateRef(),
      storySystem: this.storySystem.getStateRef(),
    });
    const stepSnapshot = this.simulationSnapshots.capture({
      stepSeconds: step,
      simulationTime: time,
      input: effectiveInput,
      replay: replayStatus,
      randomState,
      worldStateRef,
      metadata: {
        scenarioId: this.runtimeState.activeScenario,
        timeOfDay: this.runtimeState.timeOfDay,
        weather: this.runtimeState.weather,
        storyMode: this.storySystem.getStatus().mode,
      },
    });
    const divergence = this.replayDivergence.observe(stepSnapshot);
    if (replayStatus.completed && this.replayDivergence.active) this.replayDivergence.finish();
    const divergenceStatus = this.replayDivergence.getStatus();

    this.runtimeState.input = stepSnapshot.input;
    this.runtimeState.inputReplay = replayStatus;
    this.runtimeState.replayDivergence = divergenceStatus;
    this.runtimeState.entityWorld = this.entityWorld.getStatus();
    this.runtimeState.traversalWorld = this.traversalWorld.getStatus();
    this.runtimeState.snakeState = this.snakeState.getStatus();
    this.runtimeState.wildlifeSystem = this.wildlifeSystem.getStatus();
    this.runtimeState.huntingSystem = this.huntingSystem.getStatus();
    this.runtimeState.storySystem = this.storySystem.getStatus();
    this.runtimeState.random = stepSnapshot.randomState;
    this.runtimeState.simulationSteps = stepSnapshot.tick + 1;
    this.runtimeState.simulationStep = stepSnapshot;
    this.runtimeState.simulationSnapshots = this.simulationSnapshots.getStatus();

    const eventPayload = Object.freeze({
      ...stepSnapshot,
      frame: stepSnapshot.tick,
      step: stepSnapshot.stepSeconds,
      time: stepSnapshot.simulationTime,
    });
    this.bus.emit('input:step', eventPayload);
    this.legacy?.forwardInput(stepSnapshot.input);
    this.bus.emit('simulation:step', eventPayload);
    if (divergence?.divergent) this.bus.emit('replay:divergence', divergence);
  }

  #frame(timestamp) {
    const delta = this.lastFrame === 0 ? 0 : (timestamp - this.lastFrame) / 1000;
    this.lastFrame = timestamp;
    this.clock.update(delta, (step, time) => this.#fixedStep(step, time));

    const simulationStatus = this.mount.querySelector('[data-status="simulation"]');
    const simulationMode = this.clock.paused ? 'Paused' : `${this.clock.timeScale}×`;
    simulationStatus.textContent = `${simulationMode} · Tick ${this.runtimeState.simulationSteps}`;

    const inputStatus = this.mount.querySelector('[data-status="input"]');
    const activeSources = Object.entries(this.runtimeState.input.sources || {})
      .filter(([, value]) => Boolean(value))
      .map(([name]) => name);
    inputStatus.textContent = activeSources.length > 0 ? activeSources.join(', ') : 'Ready';

    const replayStatus = this.mount.querySelector('[data-status="replay"]');
    const replay = this.inputReplay.getStatus();
    const divergence = this.replayDivergence.getStatus();
    replayStatus.textContent = divergence.firstDivergence
      ? `Diverged · Tick ${divergence.firstDivergence.tick}`
      : replay.mode === 'recording'
        ? `Recording ${replay.totalFrames}`
        : replay.mode === 'replaying'
          ? `${replay.paused ? 'Paused' : 'Replay'} ${replay.cursor}/${replay.totalFrames}`
          : 'Idle';

    requestAnimationFrame((nextTimestamp) => this.#frame(nextTimestamp));
  }
}
